// NewsWave logo lockup. Mirrors mobile/src/components/logo and the
// design's official logo at mobile/assets/logo-mark.svg, AND mirrors
// web/src/components/logo.tsx byte-for-byte for the geometry.
//
// LOCKED PER CLAUDE.md §13:
// • The "N" mark is pure SVG geometry (rects + paths). No <text>, no
//   font dependency. Any change to the path data here also has to land
//   in mobile/assets/logo-mark.svg, web/src/components/logo.tsx, and
//   mobile/src/components/logo/LogoMark.tsx — never edited alone.
//
// NOT LOCKED:
// • The "NewsWave" wordmark next to the mark — it's typography, not a
//   logo asset, so it follows --font-display like any other heading.

interface LogoProps {
  readonly size?: number;
  readonly tagline?: string;
  readonly muted?: string;
  readonly color?: string;
}

export function LogoMark({ size = 32 }: { readonly size?: number }) {
  const id = `nw-mark-${size}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden
      focusable={false}
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF5C28" />
          <stop offset="100%" stopColor="#E84D1C" />
        </linearGradient>
        <linearGradient id={`${id}-wave`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <rect
        x="0"
        y="0"
        width="100"
        height="100"
        rx={size * 0.25}
        ry={size * 0.25}
        fill={`url(#${id}-bg)`}
      />
      <g>
        <path d="M 24 22 L 24 78 L 36 78 L 36 22 Z" fill="#FFFFFF" />
        <path d="M 64 22 L 64 78 L 76 78 L 76 22 Z" fill="#FFFFFF" />
        <path
          d="M 24 22 L 36 22 L 64 62 C 58 56, 50 68, 64 78 L 64 78 L 24 22 Z"
          fill={`url(#${id}-wave)`}
        />
      </g>
      <path
        d="M 28 82 C 38 78, 48 86, 58 82 C 64 80, 68 82, 72 80"
        stroke="#FFFFFF"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
      />
    </svg>
  );
}

export function LogoFull({
  size = 28,
  tagline,
  color = 'var(--text-primary)',
  muted = 'var(--text-muted)',
}: LogoProps) {
  const fontSize = Math.round(size * 0.78);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <LogoMark size={size} />
      <span
        style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.05 }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize,
            color,
            letterSpacing: '-0.01em',
          }}
        >
          NewsWave
        </span>
        {tagline && (
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: muted,
              marginTop: 2,
            }}
          >
            {tagline}
          </span>
        )}
      </span>
    </span>
  );
}
